import { Attachment } from "./Attachment";
import { VideoDto } from "./VideoTypes";

export enum CourseStatus {
  Draft = 0,
  Published = 1,
  Archived = 2
}

export const COURSE_STATUS_OPTIONS: {
  value: CourseStatus;
  label: string;
}[] = [
  { value: CourseStatus.Draft, label: "Utkast" },
  { value: CourseStatus.Published, label: "Publicerad" },
  { value: CourseStatus.Archived, label: "Arkiverad" }
];

export const COURSE_CATEGORY_OPTIONS = [
  { value: "marketplace", label: "Marketplace" },
  { value: "__system-membership", label: "GROW / Medlemskap" },
  { value: "__system-rot", label: "ROT" }
] as const;

export const DEFAULT_COURSE_CATEGORY = "marketplace";

export const isStandaloneCourseCategory = (category?: string | null) => {
  return String(category ?? "").trim().toLowerCase() === DEFAULT_COURSE_CATEGORY;
};
export enum ContentType {
  Video,
  Pdf,
  Text,
  Exercise,
   Attachment
}

export enum ChapterRole {
  Normal = 0,
  Prep = 1,
  Main = 2
}

export interface Course {
  id: number;
  title: string;
  description: string;
  category?: string;
  status: CourseStatus;
  price?: number;
  startDate?: string | null;
  finalPaymentDeadline?: string | null;
  acceptanceResponseDays?: number | null;
  registrationFee?: number | null;
  seatLimit?: number | null;
  waitlistEnabled?: boolean;
  includedCallsPerWeek?: number | null;
  prepModuleAvailableImmediately?: boolean;
  rotFullyPaidOverride?: boolean;
  isStandalone: boolean;
  createdAt: string;
  updatedAt: string;

  chapters: Chapter[];

  // optional frontend-only
  totalDurationSeconds?: number;
  enrolledUsers?: number;
  isOwnedByUser?: boolean;
}
export interface Chapter {
  id: number;
  courseId: number;
  title: string;
  description?: string;
  sortOrder: number;
  chapterRole: ChapterRole;

  contents: ChapterContent[];
}
export interface ChapterContent {
  id: number;
  chapterId: number;
  contentType: ContentType;
  sortOrder: number;

  videoId?: number | null;
  video?: VideoDto;
  attachmentId?: number | null;
  attachment?: Attachment;
  textContent?: string;
}
export interface CourseOwnership {
  courseId: number;
  purchasedAt: string;
  validUntil?: string;
}
export interface CourseWaitlistEntry {
  id: number;
  courseId: number;
  name: string;
  email: string;
  acceptanceToken?: string | null;
  invitationSentAtUtc?: string | null;
  acceptanceDeadlineUtc?: string | null;
  acceptedAtUtc?: string | null;
  expiredAtUtc?: string | null;
  createdAt: string;
}
export interface CourseAvailabilityDto {
  courseId: number;
  seatLimit?: number | null;
  enrolledUsers: number;
  reservedUsers?: number;
  waitlistEnabled: boolean;
  isFull: boolean;
}

export interface CourseVersionDto {
  id: number;
  courseId: number;
  versionNumber: number;
  changeType: string;
  title: string;
  description: string;
  category?: string | null;
  status: CourseStatus;
  price?: number | null;
  startDate?: string | null;
  finalPaymentDeadline?: string | null;
  acceptanceResponseDays?: number | null;
  registrationFee?: number | null;
  seatLimit?: number | null;
  waitlistEnabled: boolean;
  includedCallsPerWeek?: number | null;
  prepModuleAvailableImmediately: boolean;
  rotFullyPaidOverride: boolean;
  isStandalone: boolean;
  createdAtUtc: string;
}
export interface UserCourseAccess {
  courseId: number;
  hasAccess: boolean;
  accessType: 'membership' | 'program' | 'purchase' | 'admin';
}
export interface CourseListItem {
  id: number;
  title: string;
  shortDescription: string;
  thumbnailUrl?: string;
  price?: number;
  isOwned: boolean;
}
export interface CourseProgressViewDto {
  courseId: number;
  title: string;
  description: string;
  category: string;

  startDate: string;           // ISO
  accessExpiresAt?: string;    // ISO | null

  chapters: ChapterProgressDto[];
}
export interface ChapterProgressDto {
  chapterId: number;
  title: string;
  sortOrder: number;
  chapterRole: ChapterRole;

  isUnlocked: boolean;
  unlockDate: string;          // ISO

  completedAt?: string | null;
}
export type ChapterTreeNode = {
  chapterId: number;
  title: string;
  sortOrder: number;
  chapterRole: ChapterRole;

  isUnlocked: boolean;
  completedAt: string | null;

  contents: {
    contentId: number;
    contentType: ContentType;
    title?: string;
  }[];
};
export type ProductCardCta = "Buy" | "GoToCourse" | "Preview";

export interface PublicProductCardDto {
  courseId: number;

  title: string;
  description: string;
  category: string;

  thumbnailImageUrl: string;
  bannerImageUrl?: string;

  price: string;
  rating: number;
  instructor: string;

  chaptersCount: number;
  enrolledUsers: number;

  status: CourseStatus;

  isOwned: boolean;
  isPurchasable: boolean;
  cta: ProductCardCta;
}
